import { defineStore } from "pinia";

import type {
  TherapistUpdateQuery,
  TherapistStripeAccount,
  TherapistCurrent,
  AdditionalLanguage,
} from "@/Modules/Models/Domain/Therapist";
import type { TherapistState } from "./Models/State";
import type {
  TherapistCreateEducationApiQuery,
  TherapistDeleteEducationApiQuery,
  TherapistUpdateEducationApiQuery,
} from "@/Modules/Models/Api/Therapist";
import type { TherapistAction } from "@/Modules/Therapist/Models/Ui/Therapist";

import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import CreateEducationActionHandler from "./ActionHandlers/CreateEducation";
import SetTherapistActionHandler from "./ActionHandlers/SetTherapist";
import SetTherapistLevelActionHandler from "./ActionHandlers/SetTherapistLevel";
import UpdateTherapistActionHandler from "./ActionHandlers/UpdateTherapist";
import UpdateStripeAccountActionHandler from "./ActionHandlers/UpdateStripeAccount";
import UpdateTherapistCurrentsActionHandler from "./ActionHandlers/UpdateTherapistCurrents";
import UpdateEducationActionHandler from "./ActionHandlers/UpdateEducation";
import DeleteEducationActionHandler from "./ActionHandlers/DeleteEducation";
import SetTherapistActionActionHandler from "./ActionHandlers/SetTherapistAction";
import updateAdditionalLanguagesAH from "./ActionHandlers/UpdateAdditionalLanguages";

import TherapistAreaToTherapistAreaUiTransformer from "@/Modules/Therapist/Transformers/TherapistAreaToTherapistAreaUi";
import TherapistLevelToTherapistLevelUiTransformer from "./Transformers/TherapistLevelToTherapistLevelUi";

import { hasValue } from "@/Utils/Objects";

const therapistState: TherapistState = {
  isLoading: false,
  level: null,
  notification: null,
  therapist: null,
  stripeAccountLink: "",
};

export const useTherapistStore = defineStore(StateNamespace.THERAPIST, {
  state: () => ({ ...therapistState }),
  actions: {
    clear() {
      this.isLoading = false;
      this.level = null;
      this.notification = null;
      this.therapist = null;
      this.stripeAccountLink = "";
    },
    async setTherapist() {
      const setTherapistActionHandler = new SetTherapistActionHandler(this);
      await setTherapistActionHandler.handle();
    },

    async setTherapistLevel() {
      const setTherapistLevelActionHandler = new SetTherapistLevelActionHandler(this);
      await setTherapistLevelActionHandler.handle();
    },

    async updateTherapist(therapistUpdateQuery: TherapistUpdateQuery): Promise<boolean> {
      return UpdateTherapistActionHandler(therapistUpdateQuery);
    },

    async createEducation(therapistCreateEducationApiQuery: TherapistCreateEducationApiQuery) {
      const createEducationActionHandler = new CreateEducationActionHandler(this);

      await createEducationActionHandler.handle(therapistCreateEducationApiQuery);
    },

    async updateEducation(therapistUpdateEducationQuery: TherapistUpdateEducationApiQuery) {
      const updateEducationActionHandler = new UpdateEducationActionHandler(this);

      await updateEducationActionHandler.handle(therapistUpdateEducationQuery);
    },

    async deleteEducation(therapistDeleteEducationQuery: TherapistDeleteEducationApiQuery) {
      const deleteEducationActionHandler = new DeleteEducationActionHandler(this);

      await deleteEducationActionHandler.handle(therapistDeleteEducationQuery);
    },

    async renovalTherapistCapacity() {
      if (hasValue(this.therapist)) {
        const therapistUpdateQuery: TherapistUpdateQuery = {
          capacityMorning: this.therapist.capacityMorning,
          capacityAfternoon: this.therapist.capacityAfternoon,
        };

        await this.updateTherapist(therapistUpdateQuery);
      }
    },

    async updateStripeAccount(): Promise<TherapistStripeAccount> {
      const updateStripeAccountActionHandler = new UpdateStripeAccountActionHandler();
      return updateStripeAccountActionHandler.handle();
    },

    clearNotification() {
      this.notification = null;
    },

    async updateTherapistCurrents(currents: TherapistCurrent[]) {
      const updateTherapistCurrentsActionHandler = new UpdateTherapistCurrentsActionHandler(this);
      await updateTherapistCurrentsActionHandler.handle(currents);
    },

    async setTherapistAction(actionKey: TherapistAction) {
      const setTherapistActionActionHandler = new SetTherapistActionActionHandler(this);

      await setTherapistActionActionHandler.handle(actionKey);
    },

    async updateAdditionalLanguages(languages: AdditionalLanguage[]) {
      await updateAdditionalLanguagesAH(languages);
    },
  },
  getters: {
    getTherapistLevelUi(state: TherapistState) {
      if (hasValue(state.level)) {
        const therapistLevelToTherapistLevelUi = new TherapistLevelToTherapistLevelUiTransformer();

        return therapistLevelToTherapistLevelUi.transform(state.level);
      }
    },

    getTherapistAreasUi(state: TherapistState) {
      if (hasValue(state.therapist) && hasValue(state.therapist.areas)) {
        const therapistAreaToTherapistAreaUiTransformer = new TherapistAreaToTherapistAreaUiTransformer();

        return therapistAreaToTherapistAreaUiTransformer.transform(state.therapist.areas);
      } else return [];
    },

    getTherapistAreasSelected(state: TherapistState) {
      if (hasValue(state.therapist) && hasValue(state.therapist.areas)) {
        return state.therapist.areas.filter((area) => area.isActive).map((area) => area.subAreaId);
      } else return [];
    },

    getTherapistCurrentsSelected(state: TherapistState) {
      if (hasValue(state.therapist) && hasValue(state.therapist.currents)) {
        return state.therapist.currents.filter((current) => current.isActive);
      } else return [];
    },

    getTherapistLanguagesSelected(state: TherapistState) {
      if (hasValue(state.therapist) && hasValue(state.therapist.additionalLanguages)) {
        return state.therapist.additionalLanguages.filter((language) => language.isActive);
      } else return [];
    },

    showReviewDialog(): boolean {
      return !this.isLoading && hasValue(this.therapist) && this.therapist.capacityUpdated > 1;
    },

    isFastAssignment(state: TherapistState): boolean {
      if (state.therapist) {
        return state.therapist.fastAssignment;
      }

      return false;
    },
  },
});

export type TherapistStore = ReturnType<typeof useTherapistStore>;

export default useTherapistStore;
